import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { QuantityStepper } from "@/components/ui/quantity-stepper"
import { formatPence } from "@/lib/money"
import type { PageProps } from "@/types/inertia"
import "@/styles/public-wines.css"

// Card props as serialised by WineProps#wine_card_props (Rails concern).
export type WineCardData = {
  id: number
  param: string
  name: string
  producer: string
  wine_type: string
  wine_type_label: string
  mcc: boolean
  taste_profile: string | null
  taste_label: string | null
  unit_price_pence: number | null
  image_url: string | null
}

export type WineDetailData = WineCardData & {
  description: string | null
  tasting_notes: string | null
  food_pairing: string | null
  detail_image_url: string | null
}

// "Red · Dry" — type always, taste only when classified.
export function wineMetaLine(wine: Pick<WineCardData, "wine_type_label" | "taste_label">) {
  return [wine.wine_type_label, wine.taste_label].filter(Boolean).join(" · ")
}

// The one price rule: "£14.50 per bottle", or "Price on request" in italic
// muted. Never blank, never a dash, never a non-sterling figure.
export function WinePrice({ pence, className }: { pence: number | null; className?: string }) {
  if (pence === null) {
    return <span className={["wine-price wine-price-request", className].filter(Boolean).join(" ")}>Price on request</span>
  }
  return (
    <span className={["wine-price", className].filter(Boolean).join(" ")}>
      {formatPence(pence)} <span className="wine-price-unit">per bottle</span>
    </span>
  )
}

// Bottles of this wine currently in the visitor's session selection.
export function useSelectedCount(wineId: number) {
  const { props } = usePage<PageProps>()
  const line = props.selection?.lines.find((l) => l.wine_id === wineId)
  return line ? line.bottle_count : 0
}

// Posts an absolute bottle count for one wine (0 removes it). The controller
// redirects back, so Inertia re-renders the current page with the new
// shared `selection` prop.
export function setSelectionCount(wineId: number, bottleCount: number) {
  router.patch(
    `/selection/lines/${wineId}`,
    { bottle_count: bottleCount },
    { preserveState: true, preserveScroll: true },
  )
}

type WineCardProps = {
  wine: WineCardData
  mccExplainer: string
  /** Show the quantity stepper (range, finder). Off on the Home band. */
  showStepper?: boolean
  /** Current range or finder URL, carried to the detail page's back link
   *  (the server only honours same-origin /wines or /finder paths). */
  returnTo?: string
  headingLevel?: "h2" | "h3"
}

export function WineCard({
  wine,
  mccExplainer,
  showStepper = true,
  returnTo,
  headingLevel = "h3",
}: WineCardProps) {
  const selected = useSelectedCount(wine.id)
  // Optimistic local count so quick repeated clicks build on each other;
  // re-synced whenever the server's selection changes.
  const [count, setCount] = React.useState(selected)
  React.useEffect(() => setCount(selected), [selected])

  const href = returnTo
    ? `/wines/${wine.param}?return_to=${encodeURIComponent(returnTo)}`
    : `/wines/${wine.param}`
  const Heading = headingLevel
  const meta = wineMetaLine(wine)

  return (
    <article className="wine-card">
      <Link href={href} className="wine-card-media" aria-hidden="true" tabIndex={-1}>
        {wine.image_url ? (
          <img src={wine.image_url} alt="" loading="lazy" />
        ) : (
          <span className="wine-card-noimage">Image to follow</span>
        )}
      </Link>
      <div className="wine-card-body">
        <span className="wine-card-producer">{wine.producer}</span>
        <Heading className="wine-card-name">
          <Link href={href}>{wine.name}</Link>
        </Heading>
        <span className="wine-card-meta">{meta}</span>
        {wine.mcc && <span className="wine-card-note">{mccExplainer}</span>}
        <div className="wine-card-foot">
          <WinePrice pence={wine.unit_price_pence} />
          {showStepper && (
            <QuantityStepper
              size="sm"
              label={`Bottles of ${wine.name}`}
              value={count}
              onChange={(next) => {
                setCount(next)
                setSelectionCount(wine.id, next)
              }}
            />
          )}
        </div>
      </div>
    </article>
  )
}
